import React, { createContext, useContext, useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AvailabilityContext = createContext(null);

export const AvailabilityProvider = ({ children: childrenProp }) => {
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [adults, setAdults] = useState(2);
  const [childrenCount, setChildrenCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [error, setError] = useState(null);

  const fetchAvailability = useCallback(async (scrollToRooms = false) => {
    if (!checkIn || !checkOut) {
      setError('Please select check-in and check-out dates');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/api/beds24/availability?check_in=${checkIn}&check_out=${checkOut}&adults=${adults}&children=${childrenCount}`
      );
      const data = await response.json();

      if (data.success) {
        setAvailability(data);
        setError(null);
        
        // Scroll to rooms section after getting results
        if (scrollToRooms) {
          setTimeout(() => {
            const roomsSection = document.getElementById('rooms');
            if (roomsSection) {
              roomsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
      } else {
        setError(data.detail || 'Failed to fetch availability');
      }
    } catch (err) {
      setError('Unable to check availability. Please try again.');
      console.error('Availability error:', err);
    } finally {
      setLoading(false);
    }
  }, [checkIn, checkOut, adults, childrenCount]);

  const clearAvailability = useCallback(() => {
    setAvailability(null);
    setError(null);
  }, []);

  const value = {
    checkIn,
    setCheckIn,
    checkOut,
    setCheckOut,
    adults,
    setAdults,
    children: childrenCount,
    setChildren: setChildrenCount,
    loading,
    availability,
    error,
    fetchAvailability,
    clearAvailability
  };

  return (
    <AvailabilityContext.Provider value={value}>
      {childrenProp}
    </AvailabilityContext.Provider>
  );
};

export const useAvailability = () => {
  const context = useContext(AvailabilityContext);
  if (!context) {
    throw new Error('useAvailability must be used within an AvailabilityProvider');
  }
  return context;
};
